import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Breadcrumb, CtaBand } from './DemoSiteLayout';
import { SOLUTIONS, findSolution } from './solutionsData';
import SampleCall from './SampleCall';
import { T, SURFACE, HAIRLINE, Hero, GhostButton, DemoLink, Band, CenterHead, IconTile } from './siteUI';

/* =========================================================
   One solution, rendered from solutionsData.

   Every /site/solutions/:slug page is this component. Adding a solution means
   adding an entry to SOLUTIONS — no new file, no new route.
   ========================================================= */

const DemoSiteSolution = () => {
  const { slug } = useParams();
  const solution = findSolution(slug);

  /* An unknown slug goes back to the index rather than rendering an empty
     shell — otherwise a stale link shows a page with no content. */
  if (!solution) return <Navigate to="/site/solutions" replace />;

  const { name, tagline, summary, graphic: Graphic, wash, capabilities, steps, industries, sample } = solution;
  const others = SOLUTIONS.filter((s) => s.slug !== slug);

  return (
    <>
      <Breadcrumb trail={['Home', 'Solutions', name]} />

      {/* Hero */}
      <section className="grid grid-cols-1 items-center gap-10 py-8 lg:grid-cols-[1.05fr_1fr]">
        <Hero
          title={name}
          deck={tagline}
          actions={
            <>
              <DemoLink />
              <GhostButton className="!text-teal-700">
                <Link to="/site/conversational-ai" className="inline-flex items-center gap-2">
                  How it runs <ArrowRight size={15} />
                </Link>
              </GhostButton>
            </>
          }
          footerLead={
            sample && <SampleCall src={sample.src} label={sample.label} caption={sample.caption} />
          }
        >
          {summary}
        </Hero>

        <div className={`flex items-center justify-center rounded-[28px] py-10 ${wash} ${HAIRLINE}`}>
          <Graphic />
        </div>
      </section>

      {/* What it handles */}
      <section className="px-2 py-8">
        <CenterHead title={`What ${name} handles`} />
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {capabilities.map(({ name: capName, desc, icon: Icon }) => (
            <div key={capName} className={`flex flex-col p-6 ${SURFACE.cardHover}`}>
              <IconTile icon={Icon} />
              <p className="mt-3.5 text-[14.5px] font-semibold">{capName}</p>
              <p className={`mt-1.5 ${T.body}`}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it runs — a sequence, so numbers rather than icons */}
      <Band title="How it runs" centered>
        <ol className="mx-auto mt-8 grid max-w-[900px] grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <li key={step} className="flex flex-col">
              <span className="text-[12px] font-bold tabular-nums text-teal-600">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="mt-1.5 text-[13.5px] font-semibold leading-snug">{step}</span>
            </li>
          ))}
        </ol>
      </Band>

      {/* Where it fits */}
      <Band title="Where it fits" centered>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
          {industries.map((industry) => (
            <span
              key={industry}
              className={`rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-slate-700 ${HAIRLINE}`}
            >
              {industry}
            </span>
          ))}
        </div>
      </Band>

      {/* The other solutions */}
      <section className="px-2 py-8">
        <CenterHead title="Other solutions" />
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {others.map(({ slug: otherSlug, name: otherName, tagline: otherTagline, icon: Icon }) => (
            <Link
              key={otherSlug}
              to={`/site/solutions/${otherSlug}`}
              className={`flex flex-col p-5 ${SURFACE.cardHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600`}
            >
              <IconTile icon={Icon} size="sm" />
              <p className="mt-3 text-[14px] font-semibold">{otherName}</p>
              <p className={`mt-1 flex-1 ${T.micro}`}>{otherTagline}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-teal-700">
                Learn more <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-7 text-center">
          <Link
            to="/site/solutions"
            className="inline-flex items-center gap-1.5 rounded-lg text-[13px] font-semibold text-teal-700 transition-colors hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          >
            <ArrowLeft size={14} /> All solutions
          </Link>
        </div>
      </section>

      <CtaBand
        title={`See ${name} on your own calls.`}
        subtitle="Book a demo and we'll run it against a workflow you actually have."
        primary="Book a demo"
      />
    </>
  );
};

export default DemoSiteSolution;
